import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChefHat, Loader2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const ForgotPasswordPage = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!email) {
            toast.error('Veuillez entrer votre adresse email');
            return;
        }
        
        setLoading(true);
        try {
            await api.forgotPassword(email);
            setSubmitted(true);
            toast.success('Vérifiez votre boîte de réception!');
        } catch (error) {
            console.error('Forgot password error:', error);
            // Always show success to prevent email enumeration
            setSubmitted(true);
            toast.success('Vérifiez votre boîte de réception!');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
            {/* Decorative blobs */}
            <div className="blob-green top-10 -left-20 opacity-30" />
            <div className="blob-purple bottom-10 -right-20 opacity-30" />
            <div className="blob-orange top-1/2 right-10 opacity-20" />
            
            <div className="w-full max-w-md relative z-10">
                {/* Logo */}
                <Link to="/" className="flex items-center justify-center gap-3 mb-8 group">
                    <div className="p-3 bg-gradient-cameleon rounded-2xl border-2 border-foreground shadow-pop group-hover:shadow-pop-hover transition-all">
                        <ChefHat className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <span className="font-heading font-black text-2xl block">Recettes</span>
                        <span className="font-heading font-black text-2xl text-gradient-cameleon -mt-1 block">Caméléon</span>
                    </div>
                </Link>
                
                {/* Card */}
                <div className="bg-white rounded-3xl border-2 border-foreground shadow-[8px_8px_0px_0px_#1e293b] p-8">
                    {!submitted ? (
                        <>
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-foreground">
                                    <Mail className="w-8 h-8 text-amber-600" />
                                </div>
                                <h1 className="font-heading text-3xl font-black text-foreground mb-2">Mot de passe oublié?</h1>
                                <p className="text-muted-foreground">
                                    Entrez votre email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
                                </p>
                            </div>
                            
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="font-bold">Adresse email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="votre@email.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="rounded-xl border-2 border-foreground py-6 focus:shadow-pop transition-shadow"
                                        data-testid="forgot-password-email-input"
                                    />
                                </div>
                                
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full btn-pop bg-primary text-white py-6 text-lg"
                                    data-testid="forgot-password-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Envoi en cours...
                                        </>
                                    ) : (
                                        <>
                                            <Mail className="w-5 h-5 mr-2" />
                                            Envoyer le lien
                                        </>
                                    )}
                                </Button>
                            </form>
                        </>
                    ) : (
                        <div className="text-center py-4">
                            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-green-500">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                            <h2 className="font-heading text-2xl font-black text-foreground mb-2">Email envoyé!</h2>
                            <p className="text-muted-foreground mb-6">
                                Si un compte existe avec l'adresse <strong className="text-foreground">{email}</strong>, vous recevrez un email avec un lien de réinitialisation.
                            </p>
                            <p className="text-sm text-muted-foreground bg-amber-50 p-4 rounded-xl border border-amber-200">
                                <strong>Note:</strong> Le lien expire dans 1 heure. Vérifiez aussi vos courriers indésirables.
                            </p>
                        </div>
                    )}
                    
                    <div className="mt-8 text-center">
                        <Link 
                            to="/login" 
                            className="inline-flex items-center text-primary font-bold hover:underline"
                            data-testid="back-to-login-link"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Retour à la connexion
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
